import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {ApiResponse, BaseComponent, FileUpload, InputFile, UploaderComponent} from 'compob2b-libs';
import {FileUploadModule} from 'ng2-file-upload';
import {from, Observable, Subscription} from 'rxjs';
import {catchError, concatMap, finalize, map, takeUntil, tap} from 'rxjs/operators';
import {NbButtonModule, NbWindowRef} from '@nebular/theme'
import {TuiIcon} from '@taiga-ui/core'
import {HttpEventType} from '@angular/common/http'
import {ReactiveFormsModule} from '@angular/forms'

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  imports: [
    FileUploadModule,
    NbButtonModule,
    TuiIcon,
    ReactiveFormsModule,
    UploaderComponent
  ]
})
export class FileUploaderComponent extends BaseComponent {
  protected readonly filesPlural = $localize`:@@FilesPlural:файл|файла|файлов`;
  protected readonly uploadedPlural = $localize`:@@UploadedPlural:Загружен|Загружено|Загружено`;
  @Input() accept: string;
  @Input() maxFileSize: number = 25 * 1024 * 1024;
  @Input() multiple = false;
  @Input() allowedMimeType: string[];

  @Input() uploadStartCallback: (file: File, index: number) => Observable<FileUpload>;

  @Output() onFinish: EventEmitter<FileUploaderComponent> = new EventEmitter();
  @Output() onUploadOne: EventEmitter<ApiResponse<any>> = new EventEmitter();

  isUploading = false;
  finished = false;
  progress = 0;
  loaded = 0;
  subscription: Subscription;
  files: InputFile[] = [];

  constructor(@Optional() protected readonly windowRef: NbWindowRef) {
    super();
  }

  onSubmit(files: InputFile[]) {
    this.files = files;

    if (this.files.length) {
      this.isUploading = true;
      this.progress = 0;
      this.loaded = 0;

      this.subscription = from(this.files)
        .pipe(
          map(item => item['file']),
          concatMap((uploadingFile, index) =>
            this
              .uploadStartCallback(uploadingFile, index)
              .pipe(tap(response => this.fileUnloaded(response)))
          ),
          catchError(error => this.handleError(error)),
          finalize(() => this.finishAll()),
          takeUntil(this.unsubscribe)
        ).subscribe();
    }
  }

  getProgressStyle(progress: number) {
    return `stroke-dashoffset: calc(310 - (310 * ${progress}) / 100)`;
  }

  reset() {
    this.files = [];
    this.isUploading = false;
    this.finished = false;
    this.progress = 0;
    this.loaded = 0;
    this.subscription.unsubscribe();
    this.cdr.detectChanges();
  }

  fileUnloaded(data: FileUpload) {
    if (data.progress != undefined) {
      this.progress = data.progress;
    }

    if (data.event.type == HttpEventType.Response) {
      const errorMessage = this.getUploadError(data.response);
      if (errorMessage) {
        this.warningToast(errorMessage)
      } else {
        this.loaded++;
        this.onUploadOne.emit(data.response);
      }

      this.progress = 0;
    }
    this.cdr.detectChanges();
  }

  finishAll(): void {
    this.subscription.unsubscribe();
    this.finished = true;
    this.isUploading = false;
    this.onFinish.emit(this);
    this.cdr.detectChanges();
  }

  getUploadError(response: ApiResponse<any>) {
    let message = null;

    if (response != undefined && 'success' in response && !response.success) {
      const isId = typeof response.data === 'string' && /^\d+$/.test(response.data);
      const link = isId ? `. <a href="/cabinet/dam/item/edit/${response.data}" target="_blank">Загруженный ранее файл</a>` : '';
      message = 'Данный файл был загружен ранее. Загрузите другой файл' + link;
    }

    return message;
  }

}
